import {
  H3,
  Span,
  StandardTile,
  StandardTileContents,
  StandardTileFooter,
  StandardTileImage,
  StandardTileName,
  StandardTilePrice,
  VisuallyHidden,
} from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import { ModTarget } from '@vp/mod-father';
import { useFeature } from '@vp/core-ui-components';
import { ItemPreviewDisplay } from '../shared/itemPreviewDisplay/ItemPreviewDisplay';
import { LineItemActionsProvider } from '../contexts/LineItemActionsContext';
import { BuyItAgainLineItemActions } from './BuyItAgainLineItemActions';
import { BuyItAgainQuantitySelector } from './BuyItAgainQuantitySelector';
import { LineItemReorderActionStatusProvider } from '../contexts/LineItemReorderActionStatusContext';
import { BuyItAgainItemUxModWrapper } from '../uxMods/BuyItAgainItemUxModWrapper';
import type { ReorderableItem } from '../../types/ReorderableItems.types';
import { BuyItAgainItemProvider } from '../contexts/BuyItAgainItemContext';
import { BuyItAgainItemPrice } from './BuyItAgainItemPrice';
import { LineItemReorderActionFeedback } from '../shared/reorder/LineItemReorderActionFeedback';
import { ItemEditDesignAndReorderCta } from '../shared/reorder/ItemEditDesignAndReorderCta';

/**
 * Uses item contract from BOP to render items eligible for reorder on "Buy It Again" page
 * @param {*} item The item render for reorder.
 * @param displayPosition The position of the item in the BIA view.
 */
export const BuyItAgainLineItemDisplay = ({
  item,
  displayPosition,
}: {
  item: ReorderableItem;
  displayPosition: number;
}) => {
  const [orderabilityMessages = {}] = useTranslations(['orderabilityMessages']);
  const pricedQuantities = item?.merchandisingData?.pricedQuantities;
  const isMigrated = item.migrated;
  const isRetired = item.retired;

  const productName = item?.productData?.defaultDisplayName;

  // display behaviors
  const showPricing = !isRetired;
  const showItemUnavailableLabel = isRetired;
  const showQuantitySelector = !isRetired && !!pricedQuantities?.length;

  const buyNowActionEnabled = useFeature('enableExpressCheckoutOnBuyItAgain');

  /* Always show the edit and reorder action for migrated orders since the item may not have gone through Doc Conversion
  (in the case of doc conversion failure we pop a modal for the shopper to submit a design update request).
  Platform orders are only editable if an editDocumentUrl is present. Don't show the edit link if that item is retired */
  const editAndReorderActionEnabled =
    !isRetired && (isMigrated ? true : !!item.designData?.editDocumentUrl);

  return (
    <LineItemActionsProvider
      reorderActionEnabled
      editAndReorderActionEnabled={editAndReorderActionEnabled}
      buyNowActionEnabled={buyNowActionEnabled}
      displayPosition={displayPosition}
      designFeedbackActionEnabled={false}
      fulfillmentHistoryEnabledForLineItem={false}
      isGroupItem={false}
      isReorderable={true}
    >
      <BuyItAgainItemProvider item={item}>
        <BuyItAgainItemUxModWrapper item={item}>
          <LineItemReorderActionStatusProvider>
            <StandardTile>
              {/* This visually hidden product name is intended for those using assistive tech
            because assistive tech expects all info described by a heading to follow it.
            This product name is hidden from visual users to prevent redundancy since we also render
            a visible product name below to the product image */}
              {productName && (
                <H3
                  visuallyHidden
                  id={`bia-item-section-heading-${item.lineItemId}`}
                  data-testid="visually-hidden-product-name"
                >
                  {productName}
                </H3>
              )}
              <StandardTileImage backgroundColor="standard">
                <ItemPreviewDisplay useSquareDimensionsForPurcs />
              </StandardTileImage>

              <StandardTileContents>
                <StandardTileName data-testid="bia-item-tile-name">
                  {/* This aria hidden product name is intended for those viewing the visual UI
                and is hidden from assistive tech to prevent redundancy
                since we also render a visually hidden product name above the product image */}
                  <Span
                    aria-hidden
                    data-testid="bia-aria-hidden-product-name"
                    mr={3}
                  >
                    {productName}
                  </Span>
                  {editAndReorderActionEnabled && (
                    <ItemEditDesignAndReorderCta skin="link" />
                  )}
                </StandardTileName>
                <ModTarget name="merchandisedPricing">
                  <StandardTilePrice>
                    {showPricing && <BuyItAgainItemPrice />}
                    {showItemUnavailableLabel && (
                      <>
                        <Span>{orderabilityMessages.itemUnavailable}</Span>
                        <VisuallyHidden>{`: ${item.productData?.defaultDisplayName}`}</VisuallyHidden>
                      </>
                    )}
                  </StandardTilePrice>
                </ModTarget>
              </StandardTileContents>
              <StandardTileFooter>
                <LineItemReorderActionFeedback />
                <ModTarget name="merchandisedQuantitySelector">
                  {showQuantitySelector && <BuyItAgainQuantitySelector />}
                </ModTarget>
                <ModTarget name="reorderButton">
                  <BuyItAgainLineItemActions />
                </ModTarget>
              </StandardTileFooter>
            </StandardTile>
          </LineItemReorderActionStatusProvider>
        </BuyItAgainItemUxModWrapper>
      </BuyItAgainItemProvider>
    </LineItemActionsProvider>
  );
};
